'use client';

import dynamic from 'next/dynamic';
import classNames from 'classnames';

import { TEST_IDS } from '@/components/Header/constants/testIds';
import useStyles from './styles';

/**
 * Client-only boundary for the header's auth cluster, and the owner of the
 * placeholder it shows first.
 *
 * `ssr: false` on the cluster is load-bearing, not an optimisation.
 * `@picsart/runtime` pulls in growth-rc and monetization, which are
 * browser-only bundles: importing them from a component in the server graph
 * puts them in the server chunk, where their CJS/ESM interop breaks at
 * prerender time (`generateUUID is not a function`). Behind this boundary the
 * whole stack stays in the client bundle.
 *
 * The placeholder is drawn here rather than inside `HeaderAuthSection` because
 * that component does not survive long enough to be one. `RuntimeProvider`
 * swaps `<>{children}</>` for `<EmbeddedRuntimeProvider>{children}</…>` the
 * moment the federated starter lands, and a different element type at that
 * position makes React tear the subtree down and build it again — so the
 * placeholder appeared for ~60ms, vanished with the remount, and the header sat
 * empty until the runtime had booted. This slot is above that swap and never
 * remounts.
 *
 * It hands over by CSS (see `slot`'s `:has()` rule), not a shared flag: a flag
 * would have to be published from inside the subtree that remounts and would
 * lag it by an effect, showing both at once for a frame.
 */
const AuthCluster = dynamic(() => import('./AuthCluster'), { ssr: false });

const HeaderAuthSlot = () => {
  const classes = useStyles();

  return (
    <div className={classes.slot} data-testid={TEST_IDS.AUTH_SLOT}>
      <div className={classes.skeleton} aria-hidden="true" data-testid={TEST_IDS.AUTH_SKELETON}>
        <div className={classNames(classes.skeletonPill, classes.skeletonPricing)} data-testid={TEST_IDS.AUTH_SKELETON_PRICING}>
          <span className={classes.skeletonIcon} />
          <span className={classes.skeletonLabel}>Pricing</span>
        </div>
        <div className={classNames(classes.skeletonPill, classes.skeletonProjects)} data-testid={TEST_IDS.AUTH_SKELETON_PROJECTS}>
          <span className={classes.skeletonIcon} />
          <span className={classNames(classes.skeletonLabel, classes.skeletonProjectsLabel)}>Projects</span>
        </div>
        <div className={classes.skeletonAvatar} data-testid={TEST_IDS.AUTH_SKELETON_AVATAR} />
      </div>
      <AuthCluster />
    </div>
  );
};

export default HeaderAuthSlot;
